import { Button } from "./ui/button";
import AnimatedChart from "./AnimatedChart";
import neilLogo from "@/assets/neil-ai-logo-main.jpg";
import welcomeVideo from "@/assets/welcome-video.mp4";
import AIChatBox from "./AIChatBox";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const HeroSection = () => {
  return (
    <div className="animate-fade-in">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2.5 sm:gap-3.5 mb-4 mt-2">
        <img 
          src={neilLogo} 
          alt="Neil AI Global Solutions - AI Automation and PUDU Robotics Authorized Dealer" 
          className="w-auto h-12 sm:h-16 md:h-20 hover:scale-105 transition-transform duration-300"
          loading="eager"
          width="auto"
          height="80"
        />
        <span 
          className="inline-block text-[0.7rem] sm:text-[0.8rem] tracking-widest uppercase border border-[hsl(var(--line))] px-2.5 py-1.5 rounded-full glass-effect backdrop-blur-md hover:border-primary/50 transition-colors"
          style={{ color: "#d8e3ff" }}
        >
          Authorized PUDU Robotics Dealer
        </span>
      </div>

      <h1 className="font-orbitron font-bold leading-[1.05] text-[clamp(32px,5vw,64px)] mt-2.5 mb-3">
        Go Fully Autonomous with{" "}
        <span className="text-neon-teal">Neil AI</span>
      </h1>

      <p className="text-[clamp(17px,2.2vw,22px)] mb-2 max-w-[800px] leading-relaxed font-semibold text-foreground">
        Voice Automation, CRM Integration & AI Solutions that Scale Your Business While You Sleep.
      </p>
      
      <p className="text-[clamp(14px,1.8vw,16px)] mb-4 max-w-[700px] leading-relaxed text-muted-foreground">
        Empowering businesses through cutting-edge software, robotics, and intelligent automation. We streamline operations, enhance customer experiences, and reduce labor costs.
      </p>

      <div className="flex gap-3 flex-wrap items-center mt-4 mb-4">
        <Button
          asChild
          size="lg"
          className="bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 font-extrabold rounded-xl px-8 py-6 text-lg min-h-[52px] touch-manipulation transition-all duration-300 shadow-[0_0_30px_rgba(0,255,200,0.5)] hover:shadow-[0_0_40px_rgba(0,255,200,0.7)]"
        >
          <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
            📅 Book a Free AI Demo
          </a>
        </Button>
        
        <Button 
          asChild
          variant="outline"
          size="lg"
          className="border-2 border-primary/30 hover:bg-primary/10 hover:border-primary/60 font-bold rounded-xl px-8 py-6 text-lg min-h-[52px] touch-manipulation transition-all duration-300"
        >
          <a href="/#contact">📞 Contact Us</a>
        </Button>

        <AIChatBox />
      </div>

      <div className="flex flex-wrap gap-2 my-3">
        <span className="inline-flex items-center gap-2 border border-[hsl(var(--line))] bg-white/[0.04] hover:bg-white/[0.08] rounded-full px-3 py-1.5 font-semibold text-xs sm:text-sm transition-all duration-300 hover:border-primary/40">
          ✅ Authorized PUDU Dealer
        </span>
        <span className="inline-flex items-center gap-2 border border-[hsl(var(--line))] bg-white/[0.04] hover:bg-white/[0.08] rounded-full px-3 py-1.5 font-semibold text-xs sm:text-sm transition-all duration-300 hover:border-primary/40">
          🔒 Warranty & Training Included
        </span>
        <span className="inline-flex items-center gap-2 border border-[hsl(var(--line))] bg-white/[0.04] hover:bg-white/[0.08] rounded-full px-3 py-1.5 font-semibold text-xs sm:text-sm transition-all duration-300 hover:border-primary/40">
          🇺🇸 U.S. Support
        </span>
      </div>

      {/* Carousel Section */}
      <div className="my-6">
        <Carousel className="w-full max-w-4xl mx-auto">
          <CarouselContent>
            <CarouselItem>
              <div className="rounded-xl overflow-hidden glass-effect border border-[hsl(var(--line))] hover:border-primary/40 transition-all duration-300">
                <video 
                  src={welcomeVideo}
                  controls
                  loop
                  muted
                  playsInline
                  className="w-full"
                  preload="metadata"
                  onError={(e) => {
                    console.log('Video failed to load, hiding element');
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </CarouselItem>
            <CarouselItem>
              <div className="rounded-xl overflow-hidden">
                <AnimatedChart />
              </div>
            </CarouselItem>
          </CarouselContent>
          <CarouselPrevious className="hover:bg-primary/20 transition-colors" />
          <CarouselNext className="hover:bg-primary/20 transition-colors" />
        </Carousel>
      </div>
    </div>
  );
};

export default HeroSection;
